import React, { useCallback, useMemo, useState } from 'react';
import { Button, Input, showToast, Typography } from '@attentive-platform/stem-ui';
import { FormContainer } from '../../styled';
import { fetchApiData } from '../../Utils/ApiCalls';
import { COMMERCIAL_CONFIG_AUDIT_LOG, GET_DIY_ENTITLEMENT, UPDATE_DIY_ENTITLEMENT } from '../../urls';
import DIYEntitlementSection from '../UpdateEnterpriseContractBeam/DIYEntitlementSection';
import { useTradeData } from '../../Hooks/useTradeData';
import {
  buildCommercialAuditLogQuery,
  COMMERCIAL_AUDIT_DEFAULT_PAGE_SIZE,
  DIY_ENTITLEMENT_AUDIT_FIELD_NAMES,
  formatCommercialAuditAction,
  formatCommercialAuditActor,
  formatCommercialAuditWhen
} from '../../Utils/commercialAuditFormat';
import { toAllowedDiyAiTradeIds } from '../../Utils/beamEnterpriseShared';
import {
  OuterCard,
  CompanyPill,
  ConfigSummary,
  PostureTileWrapper,
  EmailRow,
  SaveContainer,
  FormBox,
  SectionBlock,
  AuditTable,
  AuditTh,
  AuditTd,
  AuditActionCell,
  AuditSectionHeader,
  AuditPaginationRow
} from './styles';

const INITIAL_POSTURE = {
  companyTier: '',
  dfyModel: '',
  accessStatus: '',
  counterPeriod: ''
};

const INITIAL_FORM = {
  diyEnabled: false,
  diyJobCap: '',
  diyUsed: 0,
  allowedTrades: []
};

const PostureTile = ({ label, value, valueStyle }) => (
  <PostureTileWrapper>
    <Typography variant='body5' color='var(--color-neutral-60)'>
      {label}
    </Typography>
    <Typography variant='body3' style={valueStyle}>
      {value || '—'}
    </Typography>
  </PostureTileWrapper>
);

const DIYEntitlement = () => {
  const [email, setEmail] = useState('');
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [posture, setPosture] = useState(INITIAL_POSTURE);
  const [form, setForm] = useState(INITIAL_FORM);
  const [proceedLoading, setProceedLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [auditRows, setAuditRows] = useState([]);
  const [auditCount, setAuditCount] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditHasNext, setAuditHasNext] = useState(false);
  const [auditSince, setAuditSince] = useState('');
  const [auditUntil, setAuditUntil] = useState('');
  const [auditLoading, setAuditLoading] = useState(false);

  const { tradesData } = useTradeData();

  const resolveTradeLabel = useMemo(() => {
    const map = new Map(tradesData.map(t => [String(t.value), t.label]));
    return id => map.get(String(id)) || id;
  }, [tradesData]);

  const fetchCommercialAuditLog = useCallback(
    async (pageNum = 1) => {
      const addr = email?.trim();
      if (!addr) {
        showToast('Company email is required to load the audit trail.', 'error');
        return;
      }

      const page = Math.max(1, Number(pageNum) || 1);
      setAuditLoading(true);
      try {
        const query = buildCommercialAuditLogQuery({
          companyEmail: addr,
          fieldNames: DIY_ENTITLEMENT_AUDIT_FIELD_NAMES,
          since: auditSince.trim() || undefined,
          until: auditUntil.trim() || undefined,
          page,
          pageSize: COMMERCIAL_AUDIT_DEFAULT_PAGE_SIZE
        });

        const res = await fetchApiData({
          apiRequest: 'GET',
          endpoint: `${COMMERCIAL_CONFIG_AUDIT_LOG}?${query}`
        });

        if (res.error) {
          showToast(res.error || 'Failed to load audit trail', 'error');
          setAuditRows([]);
          setAuditCount(0);
          setAuditHasNext(false);
          return;
        }

        const data = res.data || {};
        setAuditRows(Array.isArray(data.results) ? data.results : []);
        setAuditCount(typeof data.count === 'number' ? data.count : 0);
        setAuditHasNext(Boolean(data.next));
        setAuditPage(page);
      } catch {
        showToast('Failed to load audit trail', 'error');
        setAuditRows([]);
        setAuditCount(0);
        setAuditHasNext(false);
      } finally {
        setAuditLoading(false);
      }
    },
    [email, auditSince, auditUntil]
  );

  const diyRemaining = Math.max(0, (Number(form.diyJobCap) || 0) - (form.diyUsed || 0));

  const handleProceed = async () => {
    if (!email) {
      showToast('Please enter a company email', 'error');
      return;
    }

    setProceedLoading(true);
    try {
      const response = await fetchApiData({
        apiRequest: 'GET',
        endpoint: `${GET_DIY_ENTITLEMENT}?email=${encodeURIComponent(email.toLowerCase())}`
      });

      if (response.error) {
        showToast(response.error || 'Failed to fetch DIY entitlement data', 'error');
        return;
      }

      const data = response?.data || {};
      const cs = data.commercial_setup || {};
      const lc = data.license_counters || {};
      const st = data.summary_tiles || {};

      setCompanyId(data.company?.id ?? data.company_id ?? email);
      setCompanyName(data.company?.name || email);
      setPosture({
        companyTier: st.company_tier || '',
        dfyModel: st.dfy_model || '',
        accessStatus: st.contract_status || '',
        counterPeriod: lc.period_resets_on || ''
      });
      setForm({
        diyEnabled: cs.diy_enabled ?? false,
        diyJobCap: cs.diy_job_cap ?? lc.diy_job_cap ?? '',
        diyUsed: lc.diy_jobs_used ?? 0,
        allowedTrades: (cs.allowed_diy_ai_trades ?? []).map(t =>
          t && typeof t === 'object'
            ? { id: t.id ?? t.value, name: t.name ?? t.display_name ?? '' }
            : { id: t, name: '' }
        )
      });

      await fetchCommercialAuditLog(1);
    } catch {
      showToast('Failed to fetch DIY entitlement data', 'error');
    } finally {
      setProceedLoading(false);
    }
  };

  const handleClear = () => {
    setEmail('');
    setCompanyId(null);
    setCompanyName('');
    setPosture(INITIAL_POSTURE);
    setForm(INITIAL_FORM);
    setAuditRows([]);
    setAuditCount(0);
    setAuditPage(1);
    setAuditHasNext(false);
    setAuditSince('');
    setAuditUntil('');
  };

  const handleTradeToggle = trade => {
    setForm(prev => ({
      ...prev,
      allowedTrades: prev.allowedTrades.some(t => t.id === trade.id)
        ? prev.allowedTrades.filter(t => t.id !== trade.id)
        : [...prev.allowedTrades, trade]
    }));
  };

  const handleSave = async () => {
    if (form.diyEnabled && (form.diyJobCap === '' || Number(form.diyJobCap) < 0)) {
      showToast('Please enter a valid DIY Job Cap', 'error');
      return;
    }

    setSaveLoading(true);
    try {
      const response = await fetchApiData({
        apiRequest: 'POST',
        endpoint: UPDATE_DIY_ENTITLEMENT,
        data: {
          company_email: companyId,
          diy_enabled: form.diyEnabled,
          diy_job_cap: form.diyEnabled ? Number(form.diyJobCap) : null,
          allowed_diy_ai_trades: form.diyEnabled ? toAllowedDiyAiTradeIds(form.allowedTrades) : []
        }
      });

      if (response.error) {
        showToast(response.error || 'Failed to save DIY entitlement', 'error');
        return;
      }

      showToast('DIY entitlement saved successfully', 'success');
      await fetchCommercialAuditLog(1);
    } catch {
      showToast('Failed to save DIY entitlement', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const accessStatusColor = posture.accessStatus?.toLowerCase() === 'active' ? '#48bb78' : '#e53e3e';

  const auditRangeLabel =
    auditCount === 0
      ? 'No entries'
      : `Showing ${(auditPage - 1) * COMMERCIAL_AUDIT_DEFAULT_PAGE_SIZE + 1}–${Math.min(
          auditPage * COMMERCIAL_AUDIT_DEFAULT_PAGE_SIZE,
          auditCount
        )} of ${auditCount}`;

  return (
    <FormContainer>
      <OuterCard>
        <EmailRow>
          <Input
            value={email}
            disabled={!!companyId}
            placeholder='Enter company email'
            onChange={e => setEmail(e.target.value)}
            width='100%'
          />
          <Button
            color='primary'
            variant='contained'
            loading={proceedLoading}
            onClick={companyId ? handleClear : handleProceed}>
            {companyId ? 'Clear' : 'Next'}
          </Button>
        </EmailRow>

        {companyId && (
          <>
            <CompanyPill>Company: {companyName}</CompanyPill>

            <ConfigSummary>
              <PostureTile label='COMPANY TIER' value={posture.companyTier} />
              <PostureTile label='DFY MODEL' value={posture.dfyModel} />
              <PostureTile
                label='ACCESS STATUS'
                value={posture.accessStatus}
                valueStyle={{ color: accessStatusColor }}
              />
              <PostureTile label='COUNTER PERIOD' value={posture.counterPeriod} />
            </ConfigSummary>

            <DIYEntitlementSection
              form={form}
              setForm={setForm}
              diyRemaining={diyRemaining}
              handleTradeToggle={handleTradeToggle}
              email={email}
              onAfterResetCounters={() => fetchCommercialAuditLog(1)}
            />

            <FormBox>
              <SectionBlock style={{ marginBottom: 0 }}>
                <AuditSectionHeader>
                  <Typography variant='h6-medium'>Audit trail</Typography>
                </AuditSectionHeader>

                {auditLoading ? (
                  <Typography variant='body4' color='var(--color-neutral-60)'>
                    Loading audit trail…
                  </Typography>
                ) : auditRows.length === 0 ? (
                  <Typography variant='body4' color='var(--color-neutral-60)'>
                    No audit entries in this response.
                  </Typography>
                ) : (
                  <AuditTable>
                    <thead>
                      <tr>
                        <AuditTh>When</AuditTh>
                        <AuditTh>Actor</AuditTh>
                        <AuditTh>Action</AuditTh>
                      </tr>
                    </thead>
                    <tbody>
                      {auditRows.map(row => (
                        <tr key={row.id}>
                          <AuditTd>{formatCommercialAuditWhen(row.timestamp)}</AuditTd>
                          <AuditTd title={formatCommercialAuditActor(row.actor)}>
                            {formatCommercialAuditActor(row.actor)}
                          </AuditTd>
                          <AuditActionCell>{formatCommercialAuditAction(row, resolveTradeLabel)}</AuditActionCell>
                        </tr>
                      ))}
                    </tbody>
                  </AuditTable>
                )}

                {(auditCount > 0 || auditPage > 1) && (
                  <AuditPaginationRow>
                    <Typography variant='body5' color='var(--color-neutral-60)'>
                      {auditRangeLabel}
                    </Typography>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        variant='outlined'
                        size='small'
                        disabled={auditLoading || auditPage <= 1}
                        onClick={() => fetchCommercialAuditLog(auditPage - 1)}>
                        Previous
                      </Button>
                      <Button
                        variant='outlined'
                        size='small'
                        disabled={auditLoading || !auditHasNext}
                        onClick={() => fetchCommercialAuditLog(auditPage + 1)}>
                        Next
                      </Button>
                    </div>
                  </AuditPaginationRow>
                )}
              </SectionBlock>
            </FormBox>

            <SaveContainer>
              <Button color='primary' onClick={handleSave} loading={saveLoading}>
                Save DIY Entitlement
              </Button>
            </SaveContainer>
          </>
        )}
      </OuterCard>
    </FormContainer>
  );
};

export default DIYEntitlement;
